import type { RefObject } from 'react'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

import type {
  AccommodationDayPickerSize,
  AccommodationMobileDayCellSelectHandler,
} from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendar.types'
import AccommodationCalendarDayPicker from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarDayPicker'
import type { AccommodationDayCellVariant } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarLargeDayCell'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import type { FeaturedDateRange } from 'lib/datetime/calendarDayViews'
import { generateStaticDayViews } from 'lib/datetime/calendarDayViews'
import { generateStaticMonthCaptions } from 'lib/datetime/calendarMonthCaptions'
import noop from 'lib/function/noop'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'
import { getCalendarDayPrices } from 'selectors/offerPage/propertyOfferPageSelectors'

interface Props {
  /** Destination time zone. Determines earliest permissible check-in date. */
  propertyTimezone?: string
  featuredDateRanges?: Utils.NonEmptyArray<FeaturedDateRange> | undefined
  dayPrices?: { [isoDate: string]: App.Property.CalendarDay } | undefined
  displayLuxPlusPrice: boolean
  checkInDate?: string
  checkOutDate?: string
  /**
   * Reference to the scroll container element this picker sits in
   * This is necessary to provide so it can manage showing the correct month
   */
  scrollerRef: RefObject<HTMLElement | null>
  size: AccommodationDayPickerSize
  /**
   * The month we're picking the date for inso ISO date format
   */
  selectedMonth?: string

  /**
   * Event for when the selected dates change
   */
  onDatesChange?: AccommodationMobileDayCellSelectHandler

  availabilityVariantMap?: Map<string, AccommodationDayCellVariant>
  renderFromStart?: boolean
}

function OfferDatePicker({
  propertyTimezone,
  featuredDateRanges,
  dayPrices,
  displayLuxPlusPrice,
  checkInDate,
  scrollerRef,
  checkOutDate,
  size,
  selectedMonth,
  onDatesChange = noop,
  availabilityVariantMap,
  renderFromStart,
}: Props) {
  const intl = useIntl()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onDatesSelect = useCallback<AccommodationMobileDayCellSelectHandler>(day => {
    onDatesChange(day)
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.DateChanged)
  }, [onDatesChange, onUserAnalyticsAction])

  const month = useMemo(() => selectedMonth ? new Date(selectedMonth) : undefined, [selectedMonth])

  useEffect(() => {
    if (!month) {
      scrollerRef.current?.scrollTo({ behavior: 'auto', top: 0 })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [month])

  const staticDayViews = useMemo(() => generateStaticDayViews(propertyTimezone, featuredDateRanges, dayPrices, displayLuxPlusPrice), [propertyTimezone, featuredDateRanges, dayPrices, displayLuxPlusPrice])
  const staticMonthCaptions = useMemo(() => generateStaticMonthCaptions(intl, featuredDateRanges, dayPrices), [intl, featuredDateRanges, dayPrices])

  return <AccommodationCalendarDayPicker
    key={month ? `${month.getFullYear()}-${month.getMonth()}` : 'default'}
    startDate={checkInDate}
    endDate={checkOutDate}
    onDaySelect={onDatesSelect}
    contentRef={scrollerRef}
    views={staticDayViews}
    monthCaptions={staticMonthCaptions}
    initialMonth={month?.getMonth()}
    initialYear={month?.getFullYear()}
    size={size}
    availabilityVariantMap={availabilityVariantMap}
    renderFromStart={renderFromStart}
  />
}

type MappedProps = Pick<Props, 'dayPrices' | 'displayLuxPlusPrice'> & { open: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  dayPrices: getCalendarDayPrices(state, appState),
  displayLuxPlusPrice: !!state.displayMemberPrice,
  open: !!state.activeFilter || state.activeHeaderFilter === 'checkin',
}), OfferDatePicker, preventRenderWithClosedFilter)
